import {
  getMarketingConsentPartners,
  getMarketingConsentServices,
  getMarketingConsentText,
} from '@axo/form/configs';
import { ReloadedFormData } from '@axo/form/data-access/backend-sync';
import { loan_application, Purpose } from '@axo/shared/data-access/types';
import {
  mapNumberInputsToString,
  mapRadioInputsToBoolean,
  mapRadioInputsToString,
  mapStringInputsToNumber,
} from '../../../../utils';
import { getNullifiedCoAppData } from '../../../../utils/getNullifiedCoAppData';
import type { DenmarkFormData } from '../FormData.dk.types';
import { getCleanDebts } from './utils/getCleanDebts';
import { getTotalRefinanceAmount } from './utils/getTotalRefinanceAmount';

const FRONTEND_TOGGLES_STORAGE_KEY = 'axo.frontend.toggle';
const frontendToggles = [
  'Frontend/AddCoApplicant',
  'Frontend/HasLease',
  'Frontend/HasExtraIncome',
  'Frontend/HasCurrentYearDebt',
  'Frontend/HasDebtCollection',
] as const;

const booleanRadioInputs = [
  'LoanApplication/HasCars',
  'LoanApplication/OwnsHolidayHome',
  'LoanApplication/HasDankort',
  'LoanApplication/HasOtherPaymentCards',
  'LoanApplication/HasUnemploymentFund',
  'LoanApplication/PaymentRemarks',
  'LoanApplication/CoApplicantHasUnemploymentFund',
] as const;

const numericStringInputs = [
  'Car/0/TotalCostMonthly',
  'LoanApplication/ResidencePermit',
  'LoanApplication/RentCostMonthly',
  'LoanApplication/ExtraIncomeMonthly',
  'LoanApplication/LivingCostMonthly',
  'LoanApplication/NetIncomeMonthly',
  'LoanApplication/RegistrationNumber',
  'LoanApplication/CurrentYearDebtOutstandingAmount',
  'LoanApplication/YearsAsBankCustomer',
  'LoanApplication/CoApplicantResidencePermit',
] as const;

const incomeFields = [
  'GrossIncome',
  // 'NetIncome', //NetIncomeYearly does not exists on BE yet
  'PartnerNetIncome',
  'CoApplicantGrossIncome',
  'CoApplicantNetIncome',
];

// Map data from Backend Values to Frontend Values
const fromApi = (data: Record<string, unknown>): DenmarkFormData => ({
  ...data,
  ...mapRadioInputsToString(data, booleanRadioInputs),
  ...mapIncomes(data, 'fromApi'),
  ...mapNumberInputsToString(data, numericStringInputs),
  ...mapChildrenFromApi(data),
  ...mapCitizenshipFromApi(data),
  ...addZerosToRegNo(data),
  ...userHasChosenFlow(data),
  ...reloadFrontendToggles(data),
  ...reloadIsRefinancing(data),
});

const reloadIsRefinancing = (data: ReloadedFormData) => {
  const result = {};

  if ('LoanApplication/InitialPurpose' in data) {
    const isRefinancing =
      data['LoanApplication/InitialPurpose'] === Purpose.RefinancingLoan
        ? Purpose.RefinancingLoan
        : loan_application.Purpose.Other;
    Object.assign(result, {
      'Frontend/IsRefinancing': isRefinancing,
    });
  }

  return result;
};

const userHasChosenFlow = (data: ReloadedFormData) => {
  const result = {};

  // First element of Step 3, which means user has done chosen a flow in step 2
  if ('LoanApplication/MaritalStatus' in data) {
    Object.assign(result, {
      'Frontend/Flow': true,
    });
  }

  return result;
};

const mapIncomes = (
  data: Record<string, unknown>,
  from: 'fromApi' | 'toApi'
) => {
  const result = {};

  if (from === 'toApi') {
    for (const field of incomeFields) {
      if (`Frontend/${field}Monthly` in data) {
        Object.assign(result, {
          [`LoanApplication/${field}Yearly`]:
            Number(data[`Frontend/${field}Monthly`]) * 12,
        });
      }
    }
  }

  if (from === 'fromApi') {
    for (const field of incomeFields) {
      if (`LoanApplication/${field}Yearly` in data) {
        Object.assign(result, {
          [`Frontend/${field}Monthly`]:
            Number(data[`LoanApplication/${field}Yearly`]) / 12,
        });
      }
    }
  }

  return result;
};

const mapCitizenshipFromApi = (data: ReloadedFormData) => {
  const citizenship = data['LoanApplication/Citizenship'];

  if (citizenship === undefined) return {};

  return {
    'Frontend/IsDanish': data['LoanApplication/Citizenship'] === 'DK',
  };
};

const mapChildrenFromApi = (data: ReloadedFormData) => {
  const result = {};

  if ('AmountOfChildren' in data) {
    Object.assign(result, {
      'Frontend/AmountOfChildren': data['AmountOfChildren'],
    });

    const regex = new RegExp(/^Child\/\d\/Age$/);

    for (const [key, value] of Object.entries(data)) {
      if (key.match(regex)) {
        Object.assign(result, {
          [key]: String(value),
        });
      }
    }
  }

  return result;
};

const addZerosToRegNo = (data: ReloadedFormData) => {
  const result = {};

  if ('LoanApplication/RegistrationNumber' in data) {
    Object.assign(result, {
      'LoanApplication/RegistrationNumber': String(
        data['LoanApplication/RegistrationNumber']
      ).padStart(4, '0'),
    });
  }

  return result;
};

const addStaticValues = {
  'LoanApplication/MarketCountry': 'DK',

  'Person/MarketCountry': 'DK',
  'Customer/MarketCountry': 'DK',
  'Customer/MobilePhoneCountryCode': 45,

  'CoPerson/MarketCountry': 'DK',
  'CoCustomer/MarketCountry': 'DK',
  'CoCustomer/MobilePhoneCountryCode': 45,

  'MarketingConsent/ConsentText':
    'Ja tak, jeg vil gerne modtage markedsføring vedrørende finansielle produkter og andre tjenesteydelser fra LendMe ApS og vores samarbejdspartnere via e-mail, sms og telefonkald. Samtykket kan til enhver tid tilbagekaldes ved henvendelse til LendMe ApS.',
  'MarketingConsent/Partners': [{ name: 'All' }], // TODO: Verify
  'MarketingConsent/Services': [{ name: 'All' }], // TODO: Verify

  'LoanApplication/AcceptTermsConditions': true, // TODO: Verify
};

const addCitizenshipValues = (data: DenmarkFormData) => {
  const result = {};

  const isDanish = data['Frontend/IsDanish'];
  const addCoApplicant = data['Frontend/AddCoApplicant'];

  Object.assign(result, {
    'LoanApplication/Citizenship': isDanish
      ? 'DK'
      : data['LoanApplication/Citizenship'],
  });

  if (addCoApplicant) {
    const coAppIsDanish = data['Frontend/CoApplicantIsDanish'];

    Object.assign(result, {
      'LoanApplication/CoApplicantCitizenship': coAppIsDanish
        ? 'DK'
        : data['LoanApplication/CoApplicantCitizenship'],
      'LoanApplication/CoApplicantMaritalStatus':
        data['LoanApplication/MaritalStatus'],
    });
  }

  return result;
};

// note, consent is a part of customer, but is handled in a separate handler
const addConsentFields = (data: DenmarkFormData) => {
  const result = {};

  if (data['Ignore/MarketingConsent']) {
    Object.assign(result, {
      'MarketingConsent/MarketingConsent': {
        Version: 1,
        ConsentText: getMarketingConsentText('dk'),
        Partners: getMarketingConsentPartners('dk'),
        Services: getMarketingConsentServices('dk'),
      },
    });
  }

  if (data['Ignore/CoApplicantMarketingConsent']) {
    Object.assign(result, {
      'MarketingConsent/CoApplicantMarketingConsent': {
        Version: 1,
        ConsentText: getMarketingConsentText('dk'),
        Partners: getMarketingConsentPartners('dk'),
        Services: getMarketingConsentServices('dk'),
      },
    });
  }

  return result;
};

const reloadFrontendToggles = (data: ReloadedFormData) => {
  const storage = sessionStorage.getItem(FRONTEND_TOGGLES_STORAGE_KEY);

  let result: Record<string, 'Yes' | 'No' | undefined> = JSON.parse(
    storage || '{}'
  );

  // Magic Token flow
  if (window && new URLSearchParams(window.location.search).get('token')) {
    result = {
      'Frontend/HasLease': 'No',
      'Frontend/HasExtraIncome': 'No',
      'Frontend/HasCurrentYearDebt': 'No',
      'Frontend/HasDebtCollection': 'No',
    };
  }

  if ('Car/0/TotalCostMonthly' in data) {
    if (Number(data['Car/0/TotalCostMonthly']) > 0) {
      Object.assign(result, {
        'Frontend/HasLease': 'Yes',
      });
    }
  }

  if ('LoanApplication/ExtraIncomeMonthly' in data) {
    Object.assign(result, {
      'Frontend/HasExtraIncome': 'Yes',
    });
  }

  if ('LoanApplication/CurrentYearDebtOutstandingAmount' in data) {
    Object.assign(result, {
      'Frontend/HasCurrentYearDebt': 'Yes',
    });
  }

  if (
    data['LoanApplication/Debts']?.some(
      (debt) => debt.Type === loan_application.DebtType.Collection
    )
  ) {
    Object.assign(result, {
      'Frontend/HasDebtCollection': 'Yes',
    });
  }

  if (Object.keys(data).some((key) => key.includes('CoApplicant'))) {
    Object.assign(result, {
      'Frontend/AddCoApplicant': true,
    });
  }

  return result;
};

// Map data from Frontend Values to Backend Values
const toApi = (data: DenmarkFormData): DenmarkFormData => ({
  ...data,
  ...addStaticValues,
  ...addPurpose(data),
  ...addConsentFields(data),
  ...mapRadioInputsToBoolean(data, booleanRadioInputs),
  ...mapStringInputsToNumber(data, numericStringInputs),
  ...mapIncomes(data, 'toApi'),
  ...addCarType(data),
  ...mapChildrenToApi(data),
  ...addCitizenshipValues(data),
  ...saveFrontendToggle(data),
  ...removeCoAppData(data),
});

const removeCoAppData = (data: DenmarkFormData) => {
  const result = {};

  if (!data['Frontend/AddCoApplicant']) {
    Object.assign(result, getNullifiedCoAppData(data));
  }

  return result;
};

const saveFrontendToggle = (data: DenmarkFormData) => {
  const storage = sessionStorage.getItem(FRONTEND_TOGGLES_STORAGE_KEY);
  const store = JSON.parse(storage || '{}');

  for (const key of frontendToggles) {
    if (key in data) {
      if (store[key] !== data[key]) {
        Object.assign(store, {
          [key]: data[key],
        });
      }
    }
  }

  sessionStorage.setItem(FRONTEND_TOGGLES_STORAGE_KEY, JSON.stringify(store));

  return {};
};

const addPurpose = (data: DenmarkFormData) => {
  const result = {};

  if (
    'Frontend/IsRefinancing' in data &&
    !data['LoanApplication/InitialPurpose']
  ) {
    Object.assign(result, {
      'LoanApplication/InitialPurpose': data['Frontend/IsRefinancing'],
      'LoanApplication/Purpose': data['Frontend/IsRefinancing'],
    });
  }

  if (
    'LoanApplication/InitialPurpose' in data &&
    !data['LoanApplication/Purpose']
  ) {
    Object.assign(result, {
      'LoanApplication/Purpose': data['LoanApplication/InitialPurpose'],
    });
  }

  return result;
};

const mapChildrenToApi = (data: DenmarkFormData) => {
  const result = {};

  const regex = new RegExp(/^Child\/\d\/Age$/);

  for (const [key, value] of Object.entries(data)) {
    if (key.match(regex)) {
      Object.assign(result, {
        [key]: value === null ? null : Number(value),
      });
    }
  }

  return result;
};

const addCarType = (data: DenmarkFormData) => {
  const result = {};

  if (
    data['LoanApplication/HasCars'] === 'Yes' &&
    data['Frontend/HasLease'] === 'No'
  ) {
    Object.assign(result, {
      'Car/0/OwnershipType': loan_application.CarOwnerShipType.Owned,
    });
  }

  if (
    data['Car/0/TotalCostMonthly'] &&
    Number(data['Car/0/TotalCostMonthly']) > 0
  ) {
    Object.assign(result, {
      'Car/0/OwnershipType': loan_application.CarOwnerShipType.Leased,
    });
  }

  return result;
};

const toApiLastSync = (data: DenmarkFormData) => {
  const result: DenmarkFormData = {};

  const debts = data['LoanApplication/Debts'];
  const cleanDebts = getCleanDebts({ debts });
  const isTryingToRefinance =
    data['LoanApplication/Purpose'] ===
    loan_application.Purpose.RefinancingLoan;
  const hasNoDebts = !cleanDebts || cleanDebts.length === 0;
  const refinanceAmount = getTotalRefinanceAmount({
    debts: cleanDebts,
  });
  const hasOtherPurpose = data['LoanApplication/OtherPurpose'];
  const totalRefinanceAmountForm =
    data['LoanApplication/TotalRefinancingAmount'];
  const initialPurpose = data['LoanApplication/InitialPurpose'];

  Object.assign(result, {
    'LoanApplication/Debts': cleanDebts,
  });

  if (isTryingToRefinance) {
    if (hasNoDebts || !refinanceAmount) {
      Object.assign(result, {
        'LoanApplication/Purpose':
          initialPurpose === loan_application.Purpose.RefinancingLoan
            ? loan_application.Purpose.SpecialOccasion
            : initialPurpose,
      });
    }

    if (refinanceAmount > 0 && totalRefinanceAmountForm !== refinanceAmount) {
      Object.assign(result, {
        'LoanApplication/TotalRefinancingAmount': refinanceAmount,
      });
    }
  }

  const resultPurpose = result?.['LoanApplication/Purpose'];
  if (hasOtherPurpose) {
    if (resultPurpose) {
      if (resultPurpose !== loan_application.Purpose.Other) {
        Object.assign(result, {
          'LoanApplication/OtherPurpose': null,
        });
      }
    } else if (
      data['LoanApplication/Purpose'] !== loan_application.Purpose.Other
    ) {
      Object.assign(result, {
        'LoanApplication/OtherPurpose': null,
      });
    }
  }

  if (!('LoanApplication/RentCostMonthly' in data)) {
    Object.assign(result, {
      'LoanApplication/RentCostMonthly': 0,
    });
  }

  return { ...data, ...result };
};

export const mutators = {
  fromApi,
  toApi,
  toApiLastSync,
};
